import { useState } from "react";

import { Classes, FormGroup, Spinner } from "@blueprintjs/core";
import { Link } from "react-router";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { TOC } from "@/components/pages_logged_in/workspace-admin/WorkspaceMemberTable";
import { Avatar } from "@/components/pieces/users/Avatar";
import { useMemberMeetingsQuery } from "@/graphql";
import { StatsMember } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";
import { formatCredits } from "@/utils/string";

const FETCH_LIMIT = 10;

export function MemberMeetingsList({ user }: { user: StatsMember["user"] }) {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: meetingData, loading } = useMemberMeetingsQuery({
    skip: !user,
    variables: {
      customerId: user && user.id,
      currentPage: currentPage,
      pageSize: FETCH_LIMIT,
      withoutCanceled: true,
    },
  });
  const { memberMeetings } = meetingData || {};
  const { totalRecords } = memberMeetings || {};
  const meetings = ensureNonNullArray(memberMeetings?.data);

  return (
    <FormGroup
      style={{ marginTop: 24, marginBottom: 0 }}
      label={<b style={{ fontSize: 20 }}>Coaching History</b>}
    >
      {loading ? (
        <Box className={Classes.SKELETON} css={{ height: 100 }}></Box>
      ) : meetings.length === 0 ? (
        <Box fontStyle="italic" color="#778397">
          None yet
        </Box>
      ) : (
        <>
          <Box className="bas" css={{ position: "relative" }} mb={2}>
            {loading ? (
              <Flex
                justifyContent="center"
                css={{
                  position: "sticky",
                  top: 80,
                  opacity: 0.5,
                  width: "100%",
                  height: 0,
                  background: "#fff",
                  zIndex: 999,
                }}
              >
                <Spinner intent="primary" />
              </Flex>
            ) : null}
            <TOC className={Classes.HTML_TABLE} style={{ minWidth: "100%" }}>
              <thead>
                <tr>
                  <th style={{ background: "#F5F5FB", top: 0 }}>Service</th>
                  <th style={{ background: "#F5F5FB", top: 0 }}>Date</th>
                  <th style={{ background: "#F5F5FB", top: 0 }}>Coach</th>
                  <th style={{ background: "#F5F5FB", top: 0 }}>Credit Cost</th>
                </tr>
              </thead>
              <tbody>
                {meetings.map((meeting) => {
                  const { product, id, coach, eventAt, creditPrice } = meeting;
                  const { agentProfile } = coach || {};
                  const { coachSlug } = agentProfile || {};
                  const { name: productName, specialty } = product || {};
                  const { name: specialtyName } = specialty || {};
                  const creditText = formatCredits(creditPrice);
                  return (
                    <tr key={`meeting-item-${id}`}>
                      <td>
                        <Flex flexDirection="column">
                          <Box>{specialtyName}</Box>
                          <Box css={{ color: "#778397", fontSize: 14 }}>
                            {productName}
                          </Box>
                        </Flex>
                      </td>
                      <td>
                        <Box css={{ color: "#778397" }}>
                          {formatDate(eventAt, "MMM d, yyyy")}
                        </Box>
                      </td>
                      <td>
                        <Tooltip content={coach?.name}>
                          <Link target="_blank" to={`/@${coachSlug}`}>
                            <Avatar size={24} user={coach} />
                          </Link>
                        </Tooltip>
                      </td>
                      <td>
                        <Flex alignItems="center">
                          <EmbedSvg src={CreditSVG} height={14} width={14} />
                          <Box ml={2}>{creditText}</Box>
                        </Flex>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TOC>
          </Box>
          <Paging
            currentPage={currentPage}
            pageSize={FETCH_LIMIT}
            setCurrentPage={(index) => {
              setCurrentPage(index);
            }}
            totalRecords={totalRecords ?? 0}
            isSticky={true}
          />
        </>
      )}
    </FormGroup>
  );
}
